
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { lookupBortle } from '@/utils/bortleLookup';

interface Location {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string | null;
  created_at?: string;
}

export const useLocationManagement = (selectedLocation: Location | null) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentLocation, setCurrentLocation] = useState<Location | null>(selectedLocation);
  const [isNewLocationDialogOpen, setIsNewLocationDialogOpen] = useState(false);
  const [isFavoriteDialogOpen, setIsFavoriteDialogOpen] = useState(false);
  const [favoriteFormData, setFavoriteFormData] = useState({
    favoriteName: '',
    notes: ''
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Update current location when selectedLocation prop changes
  useEffect(() => {
    setCurrentLocation(selectedLocation);
  }, [selectedLocation]);

  const handleNewLocationSelect = (location: { name: string; latitude: number; longitude: number } | null) => {
    if (location) {
      const newLocation: Location = {
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        notes: null
      };
      
      setCurrentLocation(newLocation);
      setIsNewLocationDialogOpen(false);
      
      toast({
        title: "Location Set",
        description: `Calendar updated for: ${location.name}`,
      });
    }
  };

  const handleStarLocation = async () => {
    if (!user || !currentLocation) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to save favorite locations',
        variant: 'destructive',
      });
      return;
    }

    // Check if this location is already a favorite by coordinates
    try {
      const { data: existingFavorite, error: checkError } = await supabase
        .from('favorite_locations')
        .select('id')
        .eq('user_id', user.id)
        .eq('latitude', currentLocation.latitude)
        .eq('longitude', currentLocation.longitude)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing favorite:', checkError);
        throw checkError;
      }

      if (existingFavorite) {
        toast({
          title: 'Already a Favorite',
          description: 'This location is already in your favorites!',
          variant: 'destructive',
        });
        return;
      }
    } catch (error) {
      console.error('Error checking for existing favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to check if location is already favorited',
        variant: 'destructive',
      });
      return;
    }

    if (!favoriteFormData.favoriteName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter a name for this favorite location',
        variant: 'destructive',
      });
      return;
    }

    try {
      const bortle = await lookupBortle(currentLocation.latitude, currentLocation.longitude).catch(() => null);

      const { error } = await supabase
        .from('favorite_locations')
        .insert({
          user_id: user.id,
          name: currentLocation.name,
          display_name: favoriteFormData.favoriteName,
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          notes: favoriteFormData.notes || null,
          bortle_class: bortle,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Location added to favorites!',
      });
      
      setFavoriteFormData({ favoriteName: '', notes: '' });
      setIsFavoriteDialogOpen(false);
      
      // Trigger a refresh to update the favorite status
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to save location to favorites',
        variant: 'destructive',
      });
    }
  };

  return {
    currentLocation,
    isNewLocationDialogOpen,
    setIsNewLocationDialogOpen,
    isFavoriteDialogOpen,
    setIsFavoriteDialogOpen,
    favoriteFormData,
    setFavoriteFormData,
    handleNewLocationSelect,
    handleStarLocation,
    refreshTrigger
  };
};
